// components/Chat/cableClient.ts
import { createConsumer } from "@rails/actioncable";

let consumer = null;
let consumerIdentity = null;
const subs = new Map();

const logWs = (...args) => {
  if (typeof console !== "undefined") {
    console.log("[WS]", ...args);
  }
};

export function getConsumer(url) {
  if (!consumer) {
    consumer = createConsumer(url);
  }
  return consumer;
}

// The cable connection authenticates from the session cookie exactly once, when
// the socket opens, and `consumer` is cached for the life of the page. So after
// a client-side login the socket keeps the *previous* account's identity: it
// listens on that account's streams and never hears the current one's. Call
// this whenever the signed-in identity is known; a change tears the socket down
// so the next subscription reconnects and re-authenticates.
export function syncCableIdentity(identity) {
  const next = identity ?? null;
  if (consumerIdentity !== null && consumerIdentity !== next) {
    logWs("Identity changed, reconnecting", consumerIdentity, "->", next);
    disconnectAll();
  }
  consumerIdentity = next;
}

function clearHeartbeat(sub) {
  if (sub && sub._aliveTimer) {
    clearInterval(sub._aliveTimer);
    sub._aliveTimer = null;
  }
}

function buildCallbacks(callbacks) {
  return {
    connected() {
      callbacks?.connected?.call(this);
      // (re)start heartbeat here only after confirm
      clearHeartbeat(this);
      try { this.perform("alive"); } catch {}
      this._aliveTimer = setInterval(() => {
        // optional guard if consumer is gone
        if (!this.consumer || !this.identifier) return;
        try { this.perform("alive"); } catch {}
      }, 5000);
    },
    disconnected() {
      // socket level disconnect
      clearHeartbeat(this);
      callbacks?.disconnected?.call(this);
    },
    rejected() {
      clearHeartbeat(this);
      callbacks?.rejected?.call(this);
    },
    received(data) {
      callbacks?.received?.call(this, data);
    },
  };
}

function applyCallbacks(sub, callbacks) {
  const wrapped = buildCallbacks(callbacks);
  sub.connected = wrapped.connected;
  sub.disconnected = wrapped.disconnected;
  sub.rejected = wrapped.rejected;
  sub.received = wrapped.received;
}

export function ensureSubscription(key, params, callbacks, options = {}) {
  const { force = false } = options || {};
  const existing = subs.get(key);
  if (existing) {
    if (!force) {
      applyCallbacks(existing, callbacks);
      logWs("Reusing subscription", key);
      return existing;
    }
    logWs("Replacing subscription", key);
    // 🔑 stop old heartbeats first
    clearHeartbeat(existing);
    try { existing.unsubscribe(); } catch {}
    subs.delete(key);
  }

  const subscription = getConsumer(params.url).subscriptions.create(
    params.identifier,
    buildCallbacks(callbacks)
  );

  subs.set(key, subscription);
  return subscription;
}

export function dropSubscription(key, reason = null) {
  const sub = subs.get(key);
  if (sub) {
    clearHeartbeat(sub);
    try { sub.unsubscribe(); } catch {}
    subs.delete(key);
  }
}

export function disconnectAll() {
  logWs("Disconnecting all subscriptions", subs.size);
  subs.forEach((sub) => {
    clearHeartbeat(sub);
    try { sub.unsubscribe(); } catch {}
  });
  subs.clear();
  if (consumer) {
    try { consumer.disconnect(); } catch {}
    consumer = null;
  }
  consumerIdentity = null;
}
